import type { IsoDate } from '@erikdakoda/i18n/shared/date';
import { isIsoDate } from '@erikdakoda/i18n/shared/date';

import { aggregateDaySamples, type DayAggregateStatistic } from './aggregateDaySamples';
import { buildMetricSampleSourceId } from './buildMetricSampleSourceId';
import type { CapgoValueField } from './metricSourceInfo';
import type { SyncMetricValueInput } from './syncMetricValue';

/** One health sample after a Capgo aggregated or discrete read. */
export type HealthSampleForSync = {
  startDate: string;
  value: number;
  /** Systolic blood pressure when Capgo returns a BP sample. */
  systolic?: number;
  /** Diastolic blood pressure when Capgo returns a BP sample. */
  diastolic?: number;
  sourceId?: string;
  origin?: string;
  originName?: string;
};

/** Metric targeted by a Capgo read batch (same data type). */
export type HealthSyncMetricTarget = {
  metricId: string;

  /**
   * When set, samples are bucketed by local day and reduced with this statistic before
   * emitting one sync row per day. Foreground sync passes `sourceInfo.capgoValueAggregate`;
   * server-side raw ingest also passes `sum` for cumulative day totals.
   */
  capgoValueAggregate?: DayAggregateStatistic;
  /** Blood pressure — extract systolic/diastolic instead of `value`. */
  capgoValueField?: CapgoValueField;
};

/**
 * Maps Capgo samples for one data type onto sync rows for each target metric.
 * Does not cross-assign values across different data types — callers must issue
 * one read per data type and call this once per read.
 *
 * When a target has `capgoValueAggregate`, samples are bucketed by local day,
 * reduced with that statistic, and emitted as one row per metric per day with a
 * deterministic `{source}:{dataType}:{aggregate}:{localDay}` sourceId.
 * @param input Samples for a single Capgo data type plus the metrics that map to it.
 * @param input.samples Samples for a single Capgo data type.
 * @param input.metrics Metrics that map to that Capgo data type.
 * @param input.source MetricValue.source identity for the health platform.
 * @param input.dataType Capgo data type used when synthesizing sourceId fallbacks.
 * @returns Sync rows ready for `/api/metric/ingest`.
 */
export function mapHealthSamplesToSyncValues(input: {
  samples: readonly HealthSampleForSync[];
  metrics: readonly HealthSyncMetricTarget[];
  source: string;
  dataType: string;
}): SyncMetricValueInput[] {
  const rows: SyncMetricValueInput[] = [];
  const passthroughTargets = input.metrics.filter((metric) => !metric.capgoValueAggregate);
  const aggregateTargets = input.metrics.filter((metric) => metric.capgoValueAggregate);

  for (const sample of input.samples) {
    if (!sample.startDate) {
      continue;
    }

    const localDayCandidate = sample.startDate.slice(0, 10);
    if (!isIsoDate(localDayCandidate)) {
      continue;
    }
    const localDay: IsoDate = localDayCandidate;

    for (const metric of passthroughTargets) {
      rows.push({
        metricId: metric.metricId,
        localDay,
        measuredAt: sample.startDate,
        value: sample.value,
        source: input.source,
        sourceId:
          sample.sourceId ??
          buildMetricSampleSourceId(input.source, `${input.dataType}:${metric.metricId}`, localDay),
        origin: sample.origin,
        originName: sample.originName,
      });
    }
  }

  for (const metric of aggregateTargets) {
    const aggregate = metric.capgoValueAggregate!;
    const byDay = new Map<
      IsoDate,
      { samples: { value: number; timestamp: string }[]; latestMeasuredAt: string }
    >();

    for (const sample of input.samples) {
      if (!sample.startDate) {
        continue;
      }
      const localDayCandidate = sample.startDate.slice(0, 10);
      if (!isIsoDate(localDayCandidate)) {
        continue;
      }
      const localDay: IsoDate = localDayCandidate;
      const extracted = extractSampleValue(sample, metric.capgoValueField);
      if (extracted === null) {
        continue;
      }

      const dayBucket = byDay.get(localDay);
      if (dayBucket) {
        dayBucket.samples.push({ value: extracted, timestamp: sample.startDate });
        if (sample.startDate > dayBucket.latestMeasuredAt) {
          dayBucket.latestMeasuredAt = sample.startDate;
        }
      } else {
        byDay.set(localDay, {
          samples: [{ value: extracted, timestamp: sample.startDate }],
          latestMeasuredAt: sample.startDate,
        });
      }
    }

    for (const [localDay, dayBucket] of byDay) {
      const value = aggregateDaySamples(dayBucket.samples, aggregate);
      if (value === null) {
        continue;
      }
      rows.push({
        metricId: metric.metricId,
        localDay,
        measuredAt: dayBucket.latestMeasuredAt,
        value,
        source: input.source,
        sourceId: buildMetricSampleSourceId(
          input.source,
          `${input.dataType}:${aggregate}`,
          localDay,
        ),
      });
    }
  }

  return rows;
}

/**
 * Extracts the numeric value from a Capgo sample, optionally reading a BP field.
 * @param sample Capgo sample
 * @param field Optional blood-pressure field
 * @returns Numeric value, or null when the field is missing
 */
function extractSampleValue(
  sample: HealthSampleForSync,
  field: CapgoValueField | undefined,
): number | null {
  if (field === 'systolic') {
    return typeof sample.systolic === 'number' ? sample.systolic : null;
  }
  if (field === 'diastolic') {
    return typeof sample.diastolic === 'number' ? sample.diastolic : null;
  }
  return sample.value;
}
